import { useCallback, useEffect, useRef, useState } from 'react';

const keepRows = (rows) => rows ?? [];

// Fetch engine for numbered-page dashboards; pairs with `usePaginatedSort`.
//
// `fetchPage()` resolves to a Strapi body `{ data, meta }`. Wrap it in
// `useCallback` with the query inputs (page, sort, filters, ...) as deps: a
// new `fetchPage` means a new query, so the hook refetches. Any response
// without `meta.pagination` (loaders return null on error) sets `status` to
// 'error' and keeps the last rows.
//
// `status` is 'loading' until the first answer, so callers can show an
// empty state only for a real empty result. `reload()` refetches the
// current query, for example after a row is deleted.
//
// `initialItems` + `skipInitialFetch` let a page seeded by a router loader
// skip the duplicate request on mount.
export const usePaginatedFetch = ({
    fetchPage,
    applyPagination,
    transform = keepRows,
    initialItems = [],
    skipInitialFetch = false,
}) => {
    const [items, setItems] = useState(initialItems);
    const [status, setStatus] = useState(
        skipInitialFetch ? 'ready' : 'loading',
    );
    const [reloadKey, setReloadKey] = useState(0);
    const skipNextFetchRef = useRef(skipInitialFetch);

    // Read live so an inline transform does not trigger a refetch.
    const transformRef = useRef(transform);
    transformRef.current = transform;

    useEffect(() => {
        if (skipNextFetchRef.current) {
            skipNextFetchRef.current = false;
            return undefined;
        }
        let isActive = true;

        (async () => {
            const response = await fetchPage();
            if (!isActive) return;

            if (!response?.meta?.pagination) {
                setStatus('error');
                return;
            }
            setItems(transformRef.current(response.data));
            applyPagination(response.meta);
            setStatus('ready');
        })();

        return () => {
            isActive = false;
        };
    }, [fetchPage, reloadKey, applyPagination]);

    const reload = useCallback(() => setReloadKey((key) => key + 1), []);

    return { items, setItems, status, reload };
};
